/**
 * THE email action engine.
 *
 * Every action surface — single-row hover actions, bulk selection, drag-drop,
 * the folder picker, sender groups, thread toolbar, keyboard shortcuts, the
 * command palette — dispatches through `executeEmailAction` /
 * `executeSenderGroupAction`. The six bespoke pipelines this replaces had each
 * drifted its own copy of destination resolution, optimistic removal, undo
 * recording, and failure handling (audit themes 1, 2, 3, 7).
 *
 * The engine owns, in order:
 *   a. Context resolution per id via resolveEmailContext — caller-supplied
 *      account/folder is never trusted; unresolvable ids fail LOUDLY.
 *   b. The thread-expansion policy (encoded exactly once — see
 *      THREAD EXPANSION POLICY below).
 *   c. Per-id operation serialization — two operations on the same message
 *      execute in dispatch order (same failure class as the 2026-06-09
 *      backend outage, client-side).
 *   d. Optimistic apply with per-email prior state (source folder PER EMAIL).
 *   e. API dispatch grouped per (accountId, folderId).
 *   f. One failure path: `restoreAfterFailedAction` clears suppression marks
 *      and overlays, restores prior state, and toasts with counts. Partial
 *      bulk failures restore only the failed subset.
 *   g. Undo entries recorded AFTER successful dispatch, bound to their toast.
 *   h. Standardized toasts — success only after the request succeeds.
 */
import { toast } from 'sonner';
import { deleteDraft as apiDeleteDraft } from '@/api/drafts';
import {
  bulkDeleteEmails,
  bulkMarkEmailsRead,
  bulkMoveEmails,
  starEmail as apiStarEmail,
} from '@/api/emails';
import { performSenderBulkAction, type SenderBulkAffected } from '@/api/senders';
import { isNetworkError } from '@/api/core/client';
import { isOffline } from '@/lib/connectivity';
import {
  registerPlacementRecorder,
  useOfflineQueueStore,
  type QueuedEmailOp,
} from './offlineQueueStore';
import { isUnifiedAccountId, isUnifiedFolderId } from '@/constants/mailbox';
import { getArchiveFolderPath, getSpamFolderPath, getTrashFolderPath } from '@/lib/archiveResolver';
import { resolveEmailContext } from '@/lib/emailContext';
import { getThreadMemberIds } from '@/lib/emailMerging';
import { resolveTargetFolderForAccount } from '@/lib/folderMatching';
import { resolveSenderDescriptor, resolveSenderGroupContexts } from '@/lib/senderContexts';
import { showUndoToast } from '@/lib/undoToast';
import type { SenderGroupInfo } from '@/features/email-list/types';
import type { Email, Folder } from '@/types/email';
import {
  clearOptimisticFlags,
  clearRecentlyRemoved,
  clearSenderArchiving,
  markEmailsAsRemoved,
  markSenderAsArchiving,
  removeEmailsFromStore,
  setOptimisticFlags,
} from './emailStateHelpers';
import { useAccountStore } from './accountStore';
import { useBulkOperationsStore } from './bulkOperationsStore';
import { useEmailStore } from './emailStore';
import { useFolderStore } from './folderStore';
import { useOperationStore } from './operationStore';
import { useSenderGroupingStore } from './senderGroupingStore';
import { useUndoStore, stripEmailBody, type CustomUndoHandlers, type UndoableAction, type UndoItem } from './undoStore';
import { useRealtimeStore } from './realtimeStore';
import { mailEvents } from '@/events/mailEvents';

export type ActionKind =
  | 'archive'
  | 'delete'
  | 'move'
  | 'markRead'
  | 'markUnread'
  | 'star'
  | 'unstar'
  | 'spam';

export type ActionTarget =
  /** Explicit single messages (detail per-message toolbar). Never expanded. */
  | { kind: 'messages'; ids: string[] }
  /** List rows — the engine expands a row to its thread members. */
  | { kind: 'threadRows'; ids: string[] };

export type MoveDestination =
  /** A user-picked folder, resolved per account (role → path → name match). */
  | { type: 'folder'; folder: Folder; name: string }
  /** Pre-computed per-email destinations (drag-drop). */
  | { type: 'perEmail'; byId: Map<string, string>; name: string };

export interface ExecuteOptions {
  /** Required for kind 'move'. */
  destination?: MoveDestination;
  /** Explicit folder hint for context resolution (e.g. survey overview items). */
  contextFolderId?: string | null;
  /**
   * Explicit account hint, used ONLY when an id resolves to nothing locally
   * (e.g. flagged-survey emails that were never loaded into the store). Both
   * hints must be REAL ids — unified sentinels are rejected.
   */
  contextAccountId?: string | null;
  /** Override the success/undo toast label. */
  undoLabel?: string;
  /**
   * Companion handlers attached to the recorded undo entry (compound actions
   * like briefing "Archive & done"): the entry's undo/redo runs the email move
   * through the engine as usual, then the companion reverses/reapplies the
   * non-email half. Only applies when an undo entry is actually recorded.
   */
  companionUndo?: CustomUndoHandlers;
}

interface ActionUnit {
  id: string;
  accountId: string;
  folderId: string;
  email: Email | null;
}

const REMOVAL_KINDS = new Set<ActionKind>(['archive', 'delete', 'move', 'spam']);

// ---------------------------------------------------------------------------
// Per-id operation serialization (+ in-flight registry for realtime echoes)
// ---------------------------------------------------------------------------

const opChains = new Map<string, Promise<void>>();
const inFlightCounts = new Map<string, number>();

/**
 * True while an engine operation (action, undo, redo) is queued or running for
 * this message. The realtime move handler consults this: an `email.moved` echo
 * arriving while we have an in-flight op for the id is stale relative to our
 * own dispatch (e.g. the original archive's IMAP echo racing an undo) and must
 * not yank the row around — the op's completion settles placement.
 */
export function isEmailOperationInFlight(emailId: string): boolean {
  return (inFlightCounts.get(emailId) ?? 0) > 0;
}

/**
 * Resolves when the id's current op chain settles. The realtime move handler
 * uses this to DEFER (not drop) a move event that races an in-flight op —
 * after the chain settles the echo shield decides stale-vs-real (N10).
 */
export function whenEmailIdle(emailId: string): Promise<void> {
  return opChains.get(emailId) ?? Promise.resolve();
}

// ---------------------------------------------------------------------------
// Echo shield (E7): IMAP echoes lag the op's network window by 10–30s, so the
// in-flight registry alone can't tell a late echo of an undone action from a
// real external move. The engine records where it last PUT each id; a move
// event disagreeing with that within the shield window is the stale echo.
// ---------------------------------------------------------------------------

const ECHO_SHIELD_MS = 60_000;
const expectedPlacements = new Map<string, { accountId: string; folderRef: string | null; at: number }>();

function recordExpectedPlacement(emailId: string, accountId: string, folderRef: string | null): void {
  expectedPlacements.set(emailId, { accountId, folderRef, at: Date.now() });
  if (expectedPlacements.size > 1000) {
    const cutoff = Date.now() - ECHO_SHIELD_MS;
    expectedPlacements.forEach((entry, id) => {
      if (entry.at < cutoff) {
        expectedPlacements.delete(id);
      }
    });
  }
}

// Successful queue replays re-stamp the shield (registered as a callback so
// the queue store doesn't need a static import of this module).
registerPlacementRecorder(recordExpectedPlacement);

/**
 * Offline interception for engine dispatches. When the device is offline (or
 * the request dies with a NetworkError), the optimistic state is kept and a
 * replay descriptor is parked in the offline queue instead of rolling back —
 * the dispatch resolves as if it succeeded, with the requested ids as the
 * acted set. Replay converges the server when connectivity returns.
 */
export async function dispatchOrQueue(
  descriptor: QueuedEmailOp,
  syntheticIds: string[],
  execute: () => Promise<{ messageIds?: string[] } | void>
): Promise<{ messageIds: string[]; queued: boolean }> {
  if (isOffline()) {
    useOfflineQueueStore.getState().enqueue(descriptor);
    return { messageIds: syntheticIds, queued: true };
  }
  try {
    const result = await execute();
    return { messageIds: result?.messageIds ?? syntheticIds, queued: false };
  } catch (error) {
    if (isNetworkError(error)) {
      useOfflineQueueStore.getState().enqueue(descriptor);
      return { messageIds: syntheticIds, queued: true };
    }
    throw error;
  }
}

function canonicalFolderId(accountId: string, ref: string | null): string | null {
  if (!ref) {
    return null;
  }
  const folders = useFolderStore.getState().accountFolderCache[accountId] ?? [];
  const folder = folders.find((f) => String(f.id) === String(ref) || f.path === ref);
  return folder ? String(folder.id) : null;
}

/**
 * True when a move event's destination, within the shield window after the
 * engine's last placement of this id, POSITIVELY disagrees with that
 * placement. Unresolvable identifiers never shield (conservative: process the
 * event rather than guess).
 */
export function isStaleMoveEcho(emailId: string, accountId: string, toFolderRef: string | null): boolean {
  const entry = expectedPlacements.get(emailId);
  if (!entry) {
    return false;
  }
  if (Date.now() - entry.at > ECHO_SHIELD_MS) {
    expectedPlacements.delete(emailId);
    return false;
  }
  const expected = canonicalFolderId(entry.accountId, entry.folderRef);
  const actual = canonicalFolderId(accountId, toFolderRef);
  if (!expected || !actual) {
    return false;
  }
  return expected !== actual;
}

/**
 * Chain `task` behind any in-flight operations touching the same ids, so two
 * operations on one message execute in dispatch order (undo entries dispatch
 * through this too).
 */
function enqueueForIds<T>(rawIds: string[], task: () => Promise<T>): Promise<T> {
  const ids = Array.from(new Set(rawIds.filter(Boolean)));
  const waits = ids.map((id) => opChains.get(id) ?? Promise.resolve());
  let release!: () => void;
  const gate = new Promise<void>((resolve) => {
    release = resolve;
  });
  ids.forEach((id) => {
    opChains.set(id, gate);
    inFlightCounts.set(id, (inFlightCounts.get(id) ?? 0) + 1);
  });

  // A dispatch with no client timeout can hang indefinitely; without a
  // watchdog that parks the per-id chain AND the realtime echo skip forever
  // (N10). The watchdog releases only the serialization bookkeeping — the
  // task itself runs on and its result still reaches the caller.
  const CHAIN_RELEASE_TIMEOUT_MS = 90_000;
  let released = false;
  const releaseIds = () => {
    if (released) {
      return;
    }
    released = true;
    ids.forEach((id) => {
      const count = (inFlightCounts.get(id) ?? 1) - 1;
      if (count <= 0) {
        inFlightCounts.delete(id);
      } else {
        inFlightCounts.set(id, count);
      }
      if (opChains.get(id) === gate) {
        opChains.delete(id);
      }
    });
    release();
  };

  const run = (async () => {
    try {
      await Promise.all(waits);
      return await task();
    } finally {
      releaseIds();
    }
  })();
  const watchdog = setTimeout(() => {
    if (!released) {
      console.warn('[emailCommands] releasing wedged per-id op chain after timeout', { ids });
      releaseIds();
    }
  }, CHAIN_RELEASE_TIMEOUT_MS);
  void run.catch(() => undefined).finally(() => clearTimeout(watchdog));

  return run;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const BULK_ACTION_MAX_RETRIES = 3;
const BULK_ACTION_RETRY_DELAY_MS = 1000;

async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = BULK_ACTION_MAX_RETRIES,
  delayMs: number = BULK_ACTION_RETRY_DELAY_MS
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

function pruneFromBulkSelection(ids: string[]) {
  if (!ids.length) {
    return;
  }
  useBulkOperationsStore.setState((state) => {
    const next = new Set(state.bulkSelection);
    let changed = false;
    ids.forEach((id) => {
      if (next.delete(id)) {
        changed = true;
      }
    });
    return changed ? { bulkSelection: next } : {};
  });
}

async function reloadActiveMailbox() {
  const accountId = useAccountStore.getState().selectedAccountId;
  const folderId = useFolderStore.getState().selectedFolderId();
  if (!accountId || !folderId) {
    return;
  }
  // Route through the reload event so this respects list ownership (sender
  // group / triage) and the skip/defer machinery instead of stamping a plain
  // mailbox list over a group/label view (L1/E6).
  mailEvents.emit('mail:mailbox:reload', {
    accountId,
    folderId,
    options: { reset: true },
  });
}

/**
 * THE failure path. Clears every suppression mechanism the optimistic apply
 * armed (60s removal grace, optimistic flag overlay, sender-being-archived
 * mark), restores the recorded prior state, and surfaces a user-visible error.
 * When some entries have no snapshot to restore from, falls back to a reload.
 */
export function restoreAfterFailedAction(
  entries: Array<{ id: string; email: Email | null }>,
  opts: { senderEmail?: string | null; message?: string } = {}
): void {
  entries.forEach(({ id }) => {
    clearRecentlyRemoved(id);
    clearOptimisticFlags(id);
  });
  if (opts.senderEmail) {
    clearSenderArchiving(opts.senderEmail);
  }
  const emailStore = useEmailStore.getState();
  const snapshots = entries
    .map((entry) => entry.email)
    .filter((email): email is Email => Boolean(email));
  if (snapshots.length) {
    emailStore.applyEmailUpserts(snapshots);
  }
  emailStore.reprojectEmails();
  if (snapshots.length < entries.length) {
    // Some prior state can't be reconstructed locally — reload server truth.
    void reloadActiveMailbox();
  }
  if (opts.message) {
    toast.error(opts.message);
  }
}

function describeRemoval(kind: ActionKind, count: number, opts: ExecuteOptions): string {
  if (opts.undoLabel) {
    return opts.undoLabel;
  }
  const noun = count === 1 ? 'Email' : `${count} emails`;
  switch (kind) {
    case 'archive':
      return `${noun} archived`;
    case 'delete':
      return `${noun} deleted`;
    case 'spam':
      return `${noun} moved to spam`;
    case 'move': {
      const name = opts.destination?.type === 'folder' || opts.destination?.type === 'perEmail'
        ? opts.destination.name
        : 'folder';
      return count === 1 ? `Moved to ${name}` : `${count} emails moved to ${name}`;
    }
    default:
      return `${noun} updated`;
  }
}

function undoTypeFor(kind: ActionKind): UndoableAction['type'] {
  if (kind === 'archive') return 'archive';
  if (kind === 'delete') return 'delete';
  return 'move';
}

// ---------------------------------------------------------------------------
// executeEmailAction — messages / thread rows
// ---------------------------------------------------------------------------

export interface ActionResult {
  /** Ids whose dispatch failed or could not be resolved (already restored + toasted). */
  failedIds: string[];
}

export async function executeEmailAction(
  kind: ActionKind,
  target: ActionTarget,
  opts: ExecuteOptions = {}
): Promise<ActionResult> {
  const requestedIds = Array.from(new Set(target.ids.filter(Boolean)));
  if (!requestedIds.length) {
    return { failedIds: [] };
  }

  // (a) Resolve contexts — never trust caller-supplied account/folder.
  // (b) THREAD EXPANSION POLICY (the one place this is encoded):
  //   | target kind | action               | expansion                          |
  //   |-------------|----------------------|------------------------------------|
  //   | messages    | any                  | never expanded                     |
  //   | threadRows  | markRead/markUnread  | ALL loaded thread members          |
  //   | threadRows  | archive/delete/move/ | all loaded members — the thread    |
  //   |             | spam                 | collapse key is (account, folder,  |
  //   |             |                      | thread), so members already match  |
  //   |             |                      | the row's folder/view context      |
  //   | threadRows  | star/unstar          | the representative message only    |
  //   threadRows dispatches also send expandThreads so the SERVER widens the
  //   set to co-thread messages in the same folder that were never loaded
  //   (pagination gap) — a conversation action must take the whole
  //   conversation, Gmail-style. The response reports the full acted set.
  const emailState = useEmailStore.getState();
  const unitsById = new Map<string, ActionUnit>();
  const unresolved: string[] = [];
  const expandThreads =
    target.kind === 'threadRows' && kind !== 'star' && kind !== 'unstar';

  requestedIds.forEach((id) => {
    let ctx = resolveEmailContext(id, opts.contextFolderId ?? null);
    if (
      !ctx &&
      opts.contextAccountId &&
      opts.contextFolderId &&
      !isUnifiedAccountId(opts.contextAccountId) &&
      !isUnifiedFolderId(opts.contextFolderId)
    ) {
      // The id isn't loaded anywhere locally but the caller supplied a real,
      // explicit context (survey overview items live outside the store).
      ctx = { email: null, accountId: opts.contextAccountId, folderId: opts.contextFolderId };
    }
    if (!ctx) {
      unresolved.push(id);
      return;
    }
    if (!unitsById.has(id)) {
      unitsById.set(id, { id, accountId: ctx.accountId, folderId: ctx.folderId, email: ctx.email });
    }
    if (expandThreads && ctx.email) {
      const memberIds = getThreadMemberIds(emailState.emailIndex, ctx.email);
      memberIds.forEach((memberId) => {
        if (unitsById.has(memberId)) {
          return;
        }
        const identity = emailState.emailReverseIndex.get(memberId);
        const member = identity ? emailState.emailIndex[identity] ?? null : null;
        unitsById.set(memberId, {
          id: memberId,
          // Members share the rep's thread key (account + folder), so the
          // resolved context is valid for every member.
          accountId: member?.accountId ?? ctx.accountId,
          folderId: member?.folderId ?? ctx.folderId,
          email: member,
        });
      });
    }
  });

  if (unresolved.length) {
    toast.error(
      unresolved.length === 1
        ? "Couldn't determine the mailbox for 1 message — skipped"
        : `Couldn't determine the mailbox for ${unresolved.length} messages — skipped`
    );
  }

  const units = Array.from(unitsById.values());
  if (!units.length) {
    return { failedIds: unresolved };
  }

  const failedIds = REMOVAL_KINDS.has(kind)
    ? await runRemovalAction(kind, units, opts, expandThreads)
    : await runFlagAction(kind, units, expandThreads);
  return { failedIds: [...unresolved, ...failedIds] };
}

/** Stripped snapshot of a loaded email, or null when the id was never loaded. */
function lookupLoadedSnapshot(id: string): Email | null {
  const state = useEmailStore.getState();
  const identity = state.emailReverseIndex.get(id);
  const email = identity ? state.emailIndex[identity] ?? null : null;
  return email ? stripEmailBody(email) : null;
}

// ---------------------------------------------------------------------------
// Removal actions (archive / delete / move / spam)
// ---------------------------------------------------------------------------

interface RemovalUnit extends ActionUnit {
  /** Resolved per-email destination (null only for draft deletes / trash-less deletes). */
  destination: string | null;
}

async function runRemovalAction(
  kind: ActionKind,
  units: ActionUnit[],
  opts: ExecuteOptions,
  expandThreads = false
): Promise<string[]> {
  const folderCache = useFolderStore.getState().accountFolderCache;

  const resolveDestination = (unit: ActionUnit): string | null => {
    switch (kind) {
      case 'archive':
        return getArchiveFolderPath(unit.accountId, folderCache);
      case 'spam':
        return getSpamFolderPath(unit.accountId, folderCache);
      case 'delete':
        // Delete dispatches through the delete endpoint (server owns trash
        // semantics); the trash path here is undo bookkeeping only.
        return getTrashFolderPath(unit.accountId, folderCache);
      case 'move': {
        const dest = opts.destination;
        if (!dest) {
          return null;
        }
        if (dest.type === 'perEmail') {
          return dest.byId.get(unit.id) ?? null;
        }
        return resolveTargetFolderForAccount(dest.folder, unit.accountId, folderCache);
      }
      default:
        return null;
    }
  };

  const actionable: RemovalUnit[] = [];
  const skipped: ActionUnit[] = [];
  units.forEach((unit) => {
    const destination = resolveDestination(unit);
    if (!destination && kind !== 'delete') {
      skipped.push(unit);
      return;
    }
    actionable.push({ ...unit, destination });
  });

  if (skipped.length) {
    const plural = skipped.length === 1 ? 'message' : 'messages';
    toast.error(
      kind === 'move'
        ? `${skipped.length} ${plural} skipped — no matching folder in that account`
        : `${skipped.length} ${plural} skipped — no ${kind === 'spam' ? 'spam' : kind} folder for that account`
    );
  }
  if (!actionable.length) {
    return skipped.map((unit) => unit.id);
  }

  const ids = actionable.map((unit) => unit.id);
  // (d) Per-email prior state — source folder PER EMAIL.
  const priorById = new Map(
    actionable.map((unit) => [unit.id, unit.email ? stripEmailBody(unit.email) : null])
  );

  const operationStore = useOperationStore.getState();
  ids.forEach((id) => operationStore.markOperationPending(id));

  // Selection captured BEFORE the removal's auto-advance (N26): the undo
  // entry restores THIS, not the post-advance neighbor.
  const selectionBeforeRemoval = useEmailStore.getState().selectedEmailId;

  // Optimistic removal: stamps the 60s grace, advances selection, exits an
  // emptied sender group (the empty-check re-reads state AFTER removal).
  removeEmailsFromStore(ids);
  pruneFromBulkSelection(ids);
  useSenderGroupingStore.getState().pruneDigestEmails(ids);

  return enqueueForIds(ids, async () => {
    // (e) Dispatch grouped per (accountId, folderId, destination).
    const draftUnits =
      kind === 'delete' ? actionable.filter((unit) => unit.email?.draft) : [];
    const draftIdSet = new Set(draftUnits.map((unit) => unit.id));
    const groupable = actionable.filter((unit) => !draftIdSet.has(unit.id));

    const groups = new Map<string, { accountId: string; folderId: string; destination: string | null; units: RemovalUnit[] }>();
    groupable.forEach((unit) => {
      const key = `${unit.accountId}:${unit.folderId}:${unit.destination ?? ''}`;
      const group = groups.get(key);
      if (group) {
        group.units.push(unit);
      } else {
        groups.set(key, {
          accountId: unit.accountId,
          folderId: unit.folderId,
          destination: unit.destination,
          units: [unit],
        });
      }
    });

    const dispatches: Array<{
      units: RemovalUnit[];
      group?: { accountId: string; folderId: string; destination: string | null };
      promise: Promise<unknown>;
    }> = [];
    groups.forEach((group) => {
      const groupIds = group.units.map((unit) => unit.id);
      const promise =
        kind === 'delete'
          ? dispatchOrQueue(
              {
                op: 'bulkDelete',
                accountId: group.accountId,
                folderId: group.folderId,
                messageIds: groupIds,
                expandThreads,
              },
              groupIds,
              () => bulkDeleteEmails(group.accountId, groupIds, group.folderId, { expandThreads })
            )
          : dispatchOrQueue(
              {
                op: 'bulkMove',
                accountId: group.accountId,
                fromFolderId: group.folderId,
                destination: group.destination!,
                messageIds: groupIds,
                expandThreads,
              },
              groupIds,
              () =>
                bulkMoveEmails(group.accountId, groupIds, group.folderId, group.destination!, {
                  expandThreads,
                })
            );
      dispatches.push({
        units: group.units,
        group: { accountId: group.accountId, folderId: group.folderId, destination: group.destination },
        promise,
      });
    });
    // Drafts always route through the draft endpoint, single or bulk (B16).
    draftUnits.forEach((unit) => {
      dispatches.push({ units: [unit], promise: apiDeleteDraft(unit.accountId, unit.id) });
    });

    const settled = await Promise.allSettled(dispatches.map((d) => d.promise));
    const succeeded: RemovalUnit[] = [];
    const failed: RemovalUnit[] = [];
    let anyQueued = false;
    // Server-side conversation widening: acted-on ids beyond what we sent
    // (co-thread messages in the folder that were never loaded).
    const requestedIdSet = new Set(ids);
    const serverExtras: Array<{
      id: string;
      group: { accountId: string; folderId: string; destination: string | null };
    }> = [];
    settled.forEach((result, index) => {
      (result.status === 'fulfilled' ? succeeded : failed).push(...dispatches[index].units);
      if (result.status === 'rejected') {
        console.error(`[emailCommands] ${kind} dispatch failed`, result.reason);
        return;
      }
      if ((result.value as { queued?: boolean } | undefined)?.queued) {
        anyQueued = true;
      }
      const dispatchGroup = dispatches[index].group;
      if (!dispatchGroup) {
        return;
      }
      const actedIds = (result.value as { messageIds?: string[] } | undefined)?.messageIds;
      (actedIds ?? []).forEach((actedId) => {
        if (!requestedIdSet.has(actedId)) {
          serverExtras.push({ id: actedId, group: dispatchGroup });
        }
      });
    });

    if (serverExtras.length) {
      // The extras were part of the conversation the user acted on — mirror the
      // removal bookkeeping: echo shield, folder-scoped grace (we KNOW the
      // source folder from the dispatch group; the default path would stamp a
      // global mark for unloaded ids and hide the archived copy at its
      // destination too), and a passive prune of any loaded copies.
      serverExtras.forEach((extra) =>
        recordExpectedPlacement(extra.id, extra.group.accountId, extra.group.destination)
      );
      const extraIds = serverExtras.map((extra) => extra.id);
      markEmailsAsRemoved(
        extraIds,
        new Map(serverExtras.map((extra) => [extra.id, extra.group.folderId]))
      );
      removeEmailsFromStore(extraIds, { markRemoved: false });
      pruneFromBulkSelection(extraIds);
      useSenderGroupingStore.getState().pruneDigestEmails(extraIds);
    }

    if (succeeded.length) {
      succeeded.forEach((unit) => {
        operationStore.markOperationComplete(unit.id);
        // Echo shield (E7): remember where the engine just put this message
        // so the action's own late IMAP echo can be told apart from real
        // external moves.
        recordExpectedPlacement(unit.id, unit.accountId, unit.destination);
      });
      // (g) Undo recorded only AFTER successful dispatch. Drafts are deleted
      // permanently and trash-less deletes can't be moved back — no entry, so
      // no dead Undo button.
      const undoItems: UndoItem[] = succeeded
        .filter((unit) => !unit.email?.draft && unit.destination)
        .map((unit) => ({
          emailId: unit.id,
          accountId: unit.accountId,
          fromFolderId: unit.folderId,
          toFolderId: unit.destination!,
          email: priorById.get(unit.id) ?? null,
        }));
      // Server-widened conversation members undo right along with the row.
      serverExtras.forEach((extra) => {
        if (!extra.group.destination) {
          return;
        }
        undoItems.push({
          emailId: extra.id,
          accountId: extra.group.accountId,
          fromFolderId: extra.group.folderId,
          toFolderId: extra.group.destination,
          email: lookupLoadedSnapshot(extra.id),
        });
      });
      const description =
        describeRemoval(kind, succeeded.length + serverExtras.length, opts) +
        (anyQueued ? ' (offline — will sync)' : '');
      if (undoItems.length) {
        const entryId = useUndoStore.getState().recordAction({
          type: undoTypeFor(kind),
          items: undoItems,
          description,
          previousSelectedEmailId: selectionBeforeRemoval,
          ...(opts.companionUndo ? { companion: opts.companionUndo } : {}),
        });
        showUndoToast(description, entryId);
      } else {
        toast.success(description);
      }
    }

    if (failed.length) {
      failed.forEach((unit) => operationStore.markOperationError(unit.id));
      const verb = kind === 'delete' ? 'delete' : kind === 'archive' ? 'archive' : 'move';
      restoreAfterFailedAction(
        failed.map((unit) => ({ id: unit.id, email: priorById.get(unit.id) ?? null })),
        {
          message:
            failed.length === 1
              ? `Failed to ${verb} 1 email — restored`
              : `Failed to ${verb} ${failed.length} emails — restored`,
        }
      );
    }

    return [...skipped.map((unit) => unit.id), ...failed.map((unit) => unit.id)];
  });
}

// ---------------------------------------------------------------------------
// Flag actions (markRead / markUnread / star / unstar)
// ---------------------------------------------------------------------------

async function runFlagAction(
  kind: ActionKind,
  units: ActionUnit[],
  expandThreads = false
): Promise<string[]> {
  const isReadAction = kind === 'markRead' || kind === 'markUnread';
  const value = kind === 'markRead' || kind === 'star';
  const flags = isReadAction ? { read: value } : { starred: value };

  const emailStore = useEmailStore.getState();
  const senderStore = useSenderGroupingStore.getState();
  const operationStore = useOperationStore.getState();

  const priorById = new Map(
    units.map((unit) => [
      unit.id,
      isReadAction ? unit.email?.read : unit.email?.starred,
    ])
  );

  units.forEach((unit) => {
    // updateEmailInPlace (not updateEmail) so an open thread's in-memory
    // messages get the flag too.
    emailStore.updateEmailInPlace(unit.id, flags);
    // Dirty overlay: survive a loadEmails reset + suppress the realtime echo
    // until the server write flushes.
    setOptimisticFlags(unit.id, flags);
    if (isReadAction) {
      senderStore.updateDigestEmail(unit.id, { read: value });
    }
    operationStore.markOperationPending(unit.id);
  });

  const ids = units.map((unit) => unit.id);
  return enqueueForIds(ids, async () => {
    let dispatches: Array<{ units: ActionUnit[]; promise: Promise<unknown> }>;
    if (isReadAction) {
      const groups = new Map<string, { accountId: string; folderId: string; units: ActionUnit[] }>();
      units.forEach((unit) => {
        const key = `${unit.accountId}:${unit.folderId}`;
        const group = groups.get(key);
        if (group) {
          group.units.push(unit);
        } else {
          groups.set(key, { accountId: unit.accountId, folderId: unit.folderId, units: [unit] });
        }
      });
      dispatches = Array.from(groups.values()).map((group) => {
        const groupIds = group.units.map((unit) => unit.id);
        return {
          units: group.units,
          // expandThreads: the server also flips co-thread messages in the same
          // folder that were never loaded; their rows update via realtime
          // email.updated, so no extra bookkeeping is needed here.
          promise: dispatchOrQueue(
            {
              op: 'bulkRead',
              accountId: group.accountId,
              folderId: group.folderId,
              messageIds: groupIds,
              read: value,
              expandThreads,
            },
            groupIds,
            () =>
              bulkMarkEmailsRead(group.accountId, groupIds, value, group.folderId, {
                expandThreads,
              })
          ),
        };
      });
    } else {
      dispatches = units.map((unit) => ({
        units: [unit],
        promise: dispatchOrQueue(
          {
            op: 'star',
            accountId: unit.accountId,
            folderId: unit.folderId,
            emailId: unit.id,
            starred: value,
          },
          [unit.id],
          () => apiStarEmail(unit.accountId, unit.id, value, unit.folderId)
        ),
      }));
    }

    const settled = await Promise.allSettled(dispatches.map((d) => d.promise));
    const failedUnits: ActionUnit[] = [];
    settled.forEach((result, index) => {
      if (result.status === 'rejected') {
        failedUnits.push(...dispatches[index].units);
        console.error(`[emailCommands] ${kind} dispatch failed`, result.reason);
      } else {
        dispatches[index].units.forEach((unit) => operationStore.markOperationComplete(unit.id));
      }
    });

    if (failedUnits.length) {
      failedUnits.forEach((unit) => {
        operationStore.markOperationError(unit.id);
        clearOptimisticFlags(unit.id);
        const prior = priorById.get(unit.id);
        if (prior !== undefined) {
          const restore = isReadAction ? { read: prior } : { starred: prior };
          emailStore.updateEmailInPlace(unit.id, restore);
          if (isReadAction) {
            senderStore.updateDigestEmail(unit.id, { read: prior });
          }
        }
      });
      const what = isReadAction
        ? `mark as ${value ? 'read' : 'unread'}`
        : value
          ? 'star'
          : 'unstar';
      toast.error(
        failedUnits.length === 1
          ? `Failed to ${what} 1 email`
          : `Failed to ${what} ${failedUnits.length} emails`
      );
    }

    return failedUnits.map((unit) => unit.id);
  });
}

// ---------------------------------------------------------------------------
// Sender-group actions
// ---------------------------------------------------------------------------

/**
 * Collect every LOADED message from this sender: the group's (thread-collapsed)
 * rows, all index members from the sender (thread members hide behind reps),
 * and the open digest's cards.
 */
function collectLoadedSenderEmails(group: SenderGroupInfo, senderEmail: string | null): Map<string, Email> {
  const loadedById = new Map<string, Email>();
  group.emails.forEach((email) => {
    if (email?.id) {
      loadedById.set(email.id, email);
    }
  });
  if (senderEmail) {
    const emailIndex = useEmailStore.getState().emailIndex;
    Object.values(emailIndex).forEach((email) => {
      if (email?.from?.email?.toLowerCase().trim() === senderEmail) {
        loadedById.set(email.id, email);
      }
    });
  }
  const digest = useSenderGroupingStore.getState().senderDigest;
  if (digest && digest.group.key === group.key) {
    digest.emails.forEach((email) => {
      if (email?.id) {
        loadedById.set(email.id, email);
      }
    });
  }
  return loadedById;
}

export async function executeSenderGroupAction(
  kind: 'archive' | 'delete' | 'markRead' | 'markUnread',
  group: SenderGroupInfo
): Promise<void> {
  const groupKey = group.key;
  const senderStore = useSenderGroupingStore.getState();
  // Double-fire guard: one in-flight bulk action per group.
  if (!groupKey || senderStore.pendingGroupActions[groupKey]) {
    return;
  }

  const descriptor = resolveSenderDescriptor(group);
  const contexts = resolveSenderGroupContexts(group);
  if (!descriptor || !contexts.size) {
    toast.error("Couldn't resolve this sender — no action taken");
    return;
  }
  const senderEmail =
    (group.senderEmail || group.emails[0]?.from?.email || '').toLowerCase().trim() || null;
  const loadedById = collectLoadedSenderEmails(group, senderEmail);

  if (kind === 'markRead' || kind === 'markUnread') {
    await runSenderGroupReadAction(kind === 'markRead', descriptor, contexts, loadedById);
    return;
  }

  await runSenderGroupRemoval(kind, group, descriptor, contexts, senderEmail, loadedById);
}

async function runSenderGroupReadAction(
  read: boolean,
  descriptor: NonNullable<ReturnType<typeof resolveSenderDescriptor>>,
  contexts: ReturnType<typeof resolveSenderGroupContexts>,
  loadedById: Map<string, Email>
): Promise<void> {
  const emailStore = useEmailStore.getState();
  const senderStore = useSenderGroupingStore.getState();
  const ids = Array.from(loadedById.keys());
  const priorReadById = new Map<string, boolean>(
    Array.from(loadedById.values()).map((email) => [email.id, email.read])
  );

  ids.forEach((id) => {
    emailStore.updateEmailInPlace(id, { read });
    setOptimisticFlags(id, { read });
  });
  senderStore.setDigestReadState(ids, read);

  await enqueueForIds(ids, async () => {
    const results = await Promise.allSettled(
      Array.from(contexts.values()).map((context) =>
        performSenderBulkAction({
          accountId: context.accountId,
          folderId: context.folderId,
          sender: descriptor,
          action: read ? 'mark_read' : 'mark_unread',
        })
      )
    );
    if (results.some((result) => result.status === 'rejected')) {
      // A sender-wide op touches messages we never loaded — restore what we
      // can per-message and reload server truth for the rest.
      ids.forEach((id) => clearOptimisticFlags(id));
      useSenderGroupingStore.getState().restoreDigestReadState(priorReadById);
      toast.error(`Some emails failed to mark as ${read ? 'read' : 'unread'}. Refreshing.`);
      await reloadActiveMailbox();
    }
  });
}

async function runSenderGroupRemoval(
  kind: 'archive' | 'delete',
  group: SenderGroupInfo,
  descriptor: NonNullable<ReturnType<typeof resolveSenderDescriptor>>,
  contexts: ReturnType<typeof resolveSenderGroupContexts>,
  senderEmail: string | null,
  loadedById: Map<string, Email>
): Promise<void> {
  const groupKey = group.key;
  const folderCache = useFolderStore.getState().accountFolderCache;
  const operationStore = useOperationStore.getState();

  // Archive needs a per-account destination up front.
  const archiveByAccount = new Map<string, string>();
  if (kind === 'archive') {
    contexts.forEach(({ accountId }) => {
      if (!archiveByAccount.has(accountId)) {
        const path = getArchiveFolderPath(accountId, folderCache);
        if (path) {
          archiveByAccount.set(accountId, path);
        }
      }
    });
    if (!archiveByAccount.size) {
      toast.error('No archive folder found — nothing archived');
      return;
    }
  }

  // Accounts with no resolvable archive destination are excluded from BOTH
  // dispatch and the optimistic removal (N5): the dispatch filter alone made
  // their mail vanish for the 60s grace and resurface un-archived, with no
  // failure recorded anywhere. Mirror the non-sender path: skip before
  // removal, and say so.
  const droppedAccountIds = new Set<string>();
  if (kind === 'archive') {
    contexts.forEach(({ accountId }) => {
      if (!archiveByAccount.has(accountId)) {
        droppedAccountIds.add(accountId);
      }
    });
    if (droppedAccountIds.size) {
      toast.warning(
        `${droppedAccountIds.size} account${droppedAccountIds.size === 1 ? ' has' : 's have'} no archive folder — those messages were left in place`
      );
    }
  }

  // Prevent the realtime reload debounce from racing the optimistic removal.
  useRealtimeStore.getState().cancelScheduledReload();
  useSenderGroupingStore.getState().beginGroupAction(groupKey, kind);
  if (senderEmail) {
    markSenderAsArchiving(senderEmail);
  }

  // Selection captured BEFORE the removal's auto-advance (N26).
  const selectionBeforeRemoval = useEmailStore.getState().selectedEmailId;

  const removedUnits = Array.from(loadedById.values())
    .filter((email) => !droppedAccountIds.has(email.accountId))
    .map((email) => ({
      id: email.id,
      snapshot: stripEmailBody(email),
    }));
  if (!removedUnits.length) {
    useSenderGroupingStore.getState().endGroupAction(groupKey);
    if (senderEmail) {
      clearSenderArchiving(senderEmail);
    }
    return;
  }
  const removedIds = removedUnits.map((unit) => unit.id);

  removedIds.forEach((id) => operationStore.markOperationPending(id));
  removeEmailsFromStore(removedIds);
  pruneFromBulkSelection(removedIds);
  useSenderGroupingStore.getState().pruneDigestEmails(removedIds);
  useSenderGroupingStore.getState().clearSenderFromCaches(groupKey);

  try {
    await enqueueForIds(removedIds, async () => {
      const contextList = Array.from(contexts.values()).filter(
        (context) => kind === 'delete' || archiveByAccount.has(context.accountId)
      );
      const settled = await Promise.allSettled(
        contextList.map((context) =>
          withRetry(() =>
            performSenderBulkAction({
              accountId: context.accountId,
              folderId: context.folderId,
              sender: descriptor,
              action: kind,
              targetMailboxPath:
                kind === 'archive' ? archiveByAccount.get(context.accountId) ?? null : undefined,
            })
          )
        )
      );

      const fulfilled = settled.filter(
        (result): result is PromiseFulfilledResult<Awaited<ReturnType<typeof performSenderBulkAction>>> =>
          result.status === 'fulfilled'
      );
      const failureCount = settled.length - fulfilled.length;
      settled.forEach((result) => {
        if (result.status === 'rejected') {
          console.error(`[emailCommands] sender ${kind} dispatch failed`, result.reason);
        }
      });

      if (!fulfilled.length) {
        // Total failure — restore everything immediately (clears the sender
        // mark too, so the restore isn't filtered back out for 60s).
        removedIds.forEach((id) => operationStore.markOperationError(id));
        restoreAfterFailedAction(
          removedUnits.map((unit) => ({ id: unit.id, email: unit.snapshot })),
          {
            senderEmail,
            message: `Failed to ${kind} sender — restored`,
          }
        );
        return;
      }

      // Honest undo (C4): the server reports the COMPLETE affected id set per
      // account — including messages we never loaded — with real source/target
      // mailboxes.
      const affectedSets: SenderBulkAffected[] = fulfilled.flatMap(
        (result) => result.value.affected ?? []
      );
      const undoItems: UndoItem[] = [];
      const affectedIdSet = new Set<string>();
      affectedSets.forEach((affected) => {
        affected.messageIds.forEach((messageId) => {
          affectedIdSet.add(messageId);
          // Conversation expansion: the server takes whole threads, so the
          // affected set includes co-thread messages from OTHER senders —
          // loadedById only covers this sender, so fall back to the index.
          const loaded = loadedById.get(messageId) ?? lookupLoadedSnapshot(messageId);
          const fromFolderId = affected.sourceMailboxId ?? loaded?.folderId ?? null;
          const toFolderId =
            kind === 'archive'
              ? affected.targetMailboxId ?? archiveByAccount.get(affected.accountId) ?? null
              : affected.targetMailboxId ?? getTrashFolderPath(affected.accountId, folderCache);
          // Echo shield (E7): record the server-confirmed placement.
          recordExpectedPlacement(messageId, affected.accountId, toFolderId);
          if (fromFolderId && toFolderId) {
            undoItems.push({
              emailId: messageId,
              accountId: affected.accountId,
              fromFolderId,
              toFolderId,
              email: loaded,
            });
          }
        });
      });

      // Drop any loaded rows from the widened set that the optimistic pass
      // (sender-scoped) missed — the other participants' rows of the archived
      // conversations — rather than waiting for the realtime echo.
      const removedIdSet = new Set(removedIds);
      const expandedLoadedIds = Array.from(affectedIdSet).filter(
        (id) => !removedIdSet.has(id) && lookupLoadedSnapshot(id) !== null
      );
      if (expandedLoadedIds.length) {
        removeEmailsFromStore(expandedLoadedIds);
        pruneFromBulkSelection(expandedLoadedIds);
        useSenderGroupingStore.getState().pruneDigestEmails(expandedLoadedIds);
      }

      let affectedTotal = affectedIdSet.size;
      if (!affectedTotal) {
        // Server gave no affected info — fall back to the honest LOADED count
        // (never claim a number undo can't deliver) and build undo from the
        // loaded snapshots.
        affectedTotal = removedUnits.length;
        removedUnits.forEach((unit) => {
          const loaded = unit.snapshot;
          const destination =
            kind === 'archive'
              ? archiveByAccount.get(loaded.accountId) ?? null
              : getTrashFolderPath(loaded.accountId, folderCache);
          if (loaded.folderId && destination) {
            undoItems.push({
              emailId: unit.id,
              accountId: loaded.accountId,
              fromFolderId: loaded.folderId,
              toFolderId: destination,
              email: loaded,
            });
          }
        });
      }

      removedIds.forEach((id) => operationStore.markOperationComplete(id));

      const verb = kind === 'archive' ? 'archived' : 'deleted';
      const description =
        affectedTotal === 1 ? `Email ${verb}` : `${affectedTotal} emails ${verb}`;
      if (undoItems.length) {
        const entryId = useUndoStore.getState().recordAction({
          type: kind,
          items: undoItems,
          description,
          senderEmail,
          previousSelectedEmailId: selectionBeforeRemoval,
        });
        showUndoToast(description, entryId);
      } else {
        // No undo path resolves — show no Undo button rather than a dead one.
        toast.success(description);
      }

      if (failureCount > 0) {
        // Partial failure: restore only the loaded messages NOT covered by a
        // successful account's affected set; the sender mark must go so the
        // restore (and the reload) isn't filtered back out.
        const failedUnits = removedUnits.filter((unit) => !affectedIdSet.has(unit.id));
        restoreAfterFailedAction(
          failedUnits.map((unit) => ({ id: unit.id, email: unit.snapshot })),
          {
            senderEmail,
            message: `${failureCount} account${failureCount === 1 ? '' : 's'} failed to ${kind} — restored`,
          }
        );
        failedUnits.forEach((unit) => operationStore.markOperationError(unit.id));
      }
    });
  } finally {
    useSenderGroupingStore.getState().endGroupAction(groupKey);
  }
}

// ---------------------------------------------------------------------------
// Undo / redo execution (called by undoStore — gets serialization, suppression
// clearing AFTER the move resolves, and loud failure handling)
// ---------------------------------------------------------------------------

export async function performUndo(action: UndoableAction): Promise<boolean> {
  const ids = action.items.map((item) => item.emailId);
  const emailStore = useEmailStore.getState();

  // Optimistic restore of everything we have snapshots for. The removal grace
  // AND the sender mark must clear for the restored ids to be visible — a
  // sender-group undo was otherwise invisible until the move-back resolved
  // (N26). The original action's late IMAP echo is handled by the echo shield
  // (expected-placement check), so the mark no longer needs to outlive the
  // optimistic restore. Flag overlays still clear only after the move resolves.
  const snapshots = action.items
    .map((item) => item.email)
    .filter((email): email is Email => Boolean(email));
  ids.forEach((id) => clearRecentlyRemoved(id));
  if (action.senderEmail) {
    clearSenderArchiving(action.senderEmail);
  }
  if (snapshots.length) {
    emailStore.applyEmailUpserts(snapshots);
  }
  emailStore.reprojectEmails();

  // Restore the previous selection if it was one of the restored emails.
  if (action.previousSelectedEmailId) {
    const restored = action.items.find(
      (item) => item.emailId === action.previousSelectedEmailId && item.email
    );
    if (restored?.email) {
      void emailStore.selectEmail(
        restored.email.id,
        restored.accountId,
        restored.fromFolderId,
        { threadId: restored.email.threadId ?? null }
      );
    }
  }

  try {
    await enqueueForIds(ids, async () => {
      // Move each message back FROM its own destination TO its own original
      // folder, grouped per (account, destination, original).
      const groups = new Map<string, { accountId: string; from: string; to: string; items: UndoItem[] }>();
      action.items.forEach((item) => {
        const key = `${item.accountId}:${item.toFolderId}:${item.fromFolderId}`;
        const group = groups.get(key);
        if (group) {
          group.items.push(item);
        } else {
          groups.set(key, {
            accountId: item.accountId,
            from: item.toFolderId,
            to: item.fromFolderId,
            items: [item],
          });
        }
      });

      const groupList = Array.from(groups.values());
      const settled = await Promise.allSettled(
        groupList.map((group) => {
          const groupIds = group.items.map((item) => item.emailId);
          // Offline-queued like the forward action: an undo of a queued
          // archive must also queue, or the replayed archive would win on
          // reconnect despite the user seeing "undone" (see review E2/B).
          return dispatchOrQueue(
            {
              op: 'bulkMove',
              accountId: group.accountId,
              fromFolderId: group.from,
              destination: group.to,
              messageIds: groupIds,
              expandThreads: false,
            },
            groupIds,
            () => bulkMoveEmails(group.accountId, groupIds, group.from, group.to)
          );
        })
      );

      const failedItems: UndoItem[] = [];
      settled.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedItems.push(...groupList[index].items);
          console.error('[emailCommands] undo move failed', result.reason);
        }
      });
      if (failedItems.length) {
        // Re-remove the optimistically restored emails that did NOT move back.
        const failedRestoredIds = failedItems
          .filter((item) => item.email)
          .map((item) => item.emailId);
        if (failedRestoredIds.length) {
          removeEmailsFromStore(failedRestoredIds);
        }
        throw new Error(`${failedItems.length} message(s) failed to restore`);
      }
      // Echo shield (E2): record placement INSIDE the enqueued task — before
      // this id's chain releases — so a move echo deferred behind the undo
      // (whenEmailIdle) is classified against each item's ORIGINAL folder when
      // it re-runs. Recording it AFTER the chain released (the old placement)
      // let the deferred echo agree with the pre-undo placement and prune the
      // just-restored row.
      action.items.forEach((item) => {
        recordExpectedPlacement(item.emailId, item.accountId, item.fromFolderId);
      });
    });
  } catch (error) {
    console.error('Failed to undo action:', error);
    toast.error('Failed to undo');
    return false;
  }

  // The undo move has resolved — NOW clear the remaining suppression so the
  // restored rows survive reloads and the sender's mail is no longer hidden.
  ids.forEach((id) => {
    clearRecentlyRemoved(id);
    clearOptimisticFlags(id);
  });
  if (action.senderEmail) {
    clearSenderArchiving(action.senderEmail);
  }
  useEmailStore.getState().reprojectEmails();
  return true;
}

export async function performRedo(action: UndoableAction): Promise<boolean> {
  const ids = action.items.map((item) => item.emailId);

  // Optimistically remove again (stamps the grace — this is user-initiated).
  removeEmailsFromStore(ids);
  if (action.senderEmail) {
    markSenderAsArchiving(action.senderEmail);
  }

  try {
    await enqueueForIds(ids, async () => {
      const groups = new Map<string, { accountId: string; from: string; to: string; items: UndoItem[] }>();
      action.items.forEach((item) => {
        const key = `${item.accountId}:${item.fromFolderId}:${item.toFolderId}`;
        const group = groups.get(key);
        if (group) {
          group.items.push(item);
        } else {
          groups.set(key, {
            accountId: item.accountId,
            from: item.fromFolderId,
            to: item.toFolderId,
            items: [item],
          });
        }
      });
      const groupList = Array.from(groups.values());
      const settled = await Promise.allSettled(
        groupList.map((group) => {
          const groupIds = group.items.map((item) => item.emailId);
          return dispatchOrQueue(
            {
              op: 'bulkMove',
              accountId: group.accountId,
              fromFolderId: group.from,
              destination: group.to,
              messageIds: groupIds,
              expandThreads: false,
            },
            groupIds,
            () => bulkMoveEmails(group.accountId, groupIds, group.from, group.to)
          );
        })
      );
      const failedItems: UndoItem[] = [];
      settled.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedItems.push(...groupList[index].items);
          console.error('[emailCommands] redo move failed', result.reason);
        }
      });
      if (failedItems.length) {
        restoreAfterFailedAction(
          failedItems.map((item) => ({ id: item.emailId, email: item.email })),
          {
            senderEmail: action.senderEmail,
            message: `${failedItems.length} message(s) failed to redo`,
          }
        );
        throw new Error(`${failedItems.length} message(s) failed to redo`);
      }
      // Echo shield (E7): the redo re-placed each item at its destination.
      action.items.forEach((item) => {
        recordExpectedPlacement(item.emailId, item.accountId, item.toFolderId);
      });
    });
    return true;
  } catch (error) {
    console.error('Failed to redo action:', error);
    return false;
  }
}
